import React from "react";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import {
  Wizard,
  WizardHeader,
  WizardContent,
  WizardNav,
} from "@Shared/Components/Layouts/Wizard/Wizard";
import { Container } from "@UI/Components/Container/Container";
import { Match, On, Other } from "@Libs/Utils/Components/Match/Match";
import { Text } from "@UI/Components/Text/Text";
import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Heading } from "@UI/Components/Heading/Heading";
import { Icon } from "@UI/Components/Icon/Icon";
import { Divider } from "@UI/Components/Divider/Divider";
import { Stack } from "@UI/Components/Stack/Stack";
import { Card } from "@UI/Components/Card/Card";
import { CSSIsolation } from "@UI/Contexts/CSSIsolation";
import { ThemeProvider } from "@UI/Contexts/ThemeProvider";
import { Navigate } from "@Libs/Router/Components/Navigate/Navigate";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { Link } from "@UI/Components/Link/Link";
import { ArrowRight } from "@UI/Components/Icons/ArrowRight";
import { Calendar } from "@UI/Components/Icons/Calendar";
import { SolidInfoCircle } from "@UI/Components/Icons/SolidInfoCircle";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";
import { useFeatureFlag } from "@Libs/FeatureFlags/hooks/useFeatureFlag";
import { getLocale } from "@I18n/services/i18n";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { useSelectionRate } from "../hooks/useSelectionRate";
import { ImproveAndGoal } from "./Common/ImproveAndGoal";
import { RemainingEffortNotice } from "./Common/RemainingEffortNotice";
import { SingleTrackingCard } from "./Common/SingleTrackingCard";
import { IErrorResponse } from "@api";
import messages from "./messages";
import commonMessages from "./Common/messages";
import declineReasonMessages from "@modules/ServiceRequest/Components/messages";

const bold = (...chunks: React.ReactNode[]) => (
  <Text as="span" fontWeight="700">
    {chunks}
  </Text>
);

const ACADEMY_URLS: Record<string, string> = {
  "fr-BE":
    "https://ringtwice.be/fr/academy/taux-de-selection-moins-doffres-mais-les-bonnes",
  "nl-BE":
    "https://ringtwice.be/nl/academy/selectieratio-minder-voorstellen-maar-de-juiste",
  "fr-FR":
    "https://ringtwice.fr/academy/taux-de-selection-moins-doffres-mais-les-bonnes",
};

const CONTAINER_MAX_W = [
  "container.md",
  "container.sm",
  "container.md",
  "container.xl",
];

export const SelectionRate = () => {
  const { generateRoute } = useRoutes();
  const academyUrl = ACADEMY_URLS[getLocale()] ?? ACADEMY_URLS["fr-BE"];
  const { isEnabled: isAdditionalSuperTalentMetricsEnabled } = useFeatureFlag(
    "additional_supertalent_metrics"
  );

  const { currentUser, isLoading: isUserLoading } = useCurrentUser({
    includes: ["serviceProvider"],
  });
  const { data, isLoading, error } = useSelectionRate(
    currentUser?.serviceProvider?.id
  );

  if (isUserLoading || isLoading) return <LoadingScreen />;

  const score =
    data?.value !== null && data?.value !== undefined
      ? Math.round(Number(data.value))
      : null;
  const selectionsCount = data?.computationDetails?.selectionsCount ?? 0;
  const offersSentCount = data?.computationDetails?.offersSentCount ?? 0;
  const declineReasons = [...(data?.declineReasons ?? [])].sort(
    (a, b) => b.count - a.count
  );

  return (
    <Match value={(error as IErrorResponse)?.response?.status}>
      <On valueBetween={[400, 499]}>
        <Navigate to="NOT_FOUND" reload />
      </On>
      <On valueBetween={[500, 599]}>
        <Navigate to="SERVER_ERROR" reload />
      </On>
      <Other>
        <CSSIsolation>
          <ThemeProvider>
            <Wizard paddingTop={{ base: 5, md: 8 }}>
              <WizardNav>
                <BackLink onClick={() => history.back()} />
              </WizardNav>

              <WizardHeader marginBottom={{ base: 5, md: 3 }}>
                <Container px={{ base: 0 }} maxW={CONTAINER_MAX_W}>
                  <Heading
                    textAlign="start"
                    fontSize={{ base: "3xl", lg: "5xl" }}
                  >
                    <FormattedMessage {...messages.selectionRateTitle} />
                  </Heading>
                </Container>
              </WizardHeader>

              <WizardContent marginBottom={{ base: 5, md: 3 }}>
                <Container px={{ base: 0 }} maxW={CONTAINER_MAX_W}>
                  {/* Description + stats */}
                  <Box
                    borderBottomWidth="1px"
                    borderBottomColor="gray.300"
                    pb={{ base: 5, md: 8 }}
                  >
                    <Text
                      as="span"
                      fontSize={{ base: "md", md: "lg" }}
                      mb={{ base: 4, md: 6 }}
                      display="block"
                    >
                      <FormattedMessage {...messages.selectionRateInfos} />
                    </Text>

                    {isAdditionalSuperTalentMetricsEnabled &&
                      data?.remainingEffort !== undefined && (
                        <RemainingEffortNotice
                          context="selection_rate"
                          remainingEffort={data.remainingEffort}
                        />
                      )}

                    <Flex justifyContent="flex-end" mb={3}>
                      <Text as="span" fontSize={{ base: "sm", md: "md" }}>
                        <FormattedMessage
                          {...commonMessages.dateComputed}
                          values={{
                            calendar: () => (
                              <Icon
                                as={Calendar}
                                marginRight={1}
                                display="inline"
                              />
                            ),
                          }}
                        />
                      </Text>
                    </Flex>

                    <Stack
                      direction={["column", "row"]}
                      spacing={{ base: 2, md: 3 }}
                    >
                      <SingleTrackingCard
                        type="score"
                        value={
                          score !== null ? (
                            <FormattedMessage
                              {...commonMessages.score_value}
                              values={{ score }}
                            />
                          ) : (
                            <FormattedMessage
                              {...commonMessages.no_information}
                            />
                          )
                        }
                      />
                      <SingleTrackingCard
                        type="selectionsCount"
                        value={selectionsCount}
                      />
                      <SingleTrackingCard
                        type="offersSentCount"
                        value={offersSentCount}
                      />
                    </Stack>

                    <Divider borderColor="gray.300" my={{ base: 4, md: 6 }} />

                    <Flex justifyContent="flex-end">
                      <Link
                        as="a"
                        href={generateRoute("APPLICATIONS_REFUSED")}
                        color="pink.500"
                        fontWeight="500"
                        fontSize={{ base: "sm", md: "md" }}
                        textDecoration="none"
                        _hover={{ textDecoration: "underline" }}
                      >
                        <FormattedMessage
                          {...messages.seeClientFeedback}
                          values={{
                            RightIcon: () => (
                              <Icon
                                as={ArrowRight}
                                w={4}
                                h={4}
                                display="inline"
                                ml={1}
                              />
                            ),
                          }}
                        />
                      </Link>
                    </Flex>
                  </Box>

                  {/* Feedbacks from clients */}
                  {declineReasons.length > 0 && (
                    <Box
                      borderBottomWidth="1px"
                      borderBottomColor="gray.300"
                      py={{ base: 5, md: 8 }}
                    >
                      <Heading
                        textAlign="start"
                        fontSize={{ base: "xl", lg: "3xl" }}
                        mb={2}
                      >
                        <FormattedMessage
                          {...commonMessages.selectionRateFeedbacksTitle}
                        />
                      </Heading>
                      <Text
                        fontSize={{ base: "sm", md: "md" }}
                        mb={{ base: 4, md: 5 }}
                      >
                        <FormattedMessage
                          {...commonMessages.selectionRateFeedbacksDesc}
                          values={{ bold }}
                        />
                      </Text>
                      <Flex
                        gap={3}
                        overflowX={{ base: "auto", md: "visible" }}
                        flexWrap={{ base: "nowrap", md: "wrap" }}
                        justifyContent={{ base: "flex-start", md: "center" }}
                        pb={{ base: 2, md: 0 }}
                      >
                        {declineReasons.map((item, i) => {
                          const reasonMessage =
                            declineReasonMessages[
                              `serviceRequest.declineOffer.reasons.${item.reason}` as keyof typeof declineReasonMessages
                            ];

                          return (
                            <Card
                              key={i}
                              padding="12px"
                              flexShrink={0}
                              w={{ base: "220px", md: "auto" }}
                              minW={{ md: "200px" }}
                              h="auto"
                            >
                              <Text
                                fontWeight="600"
                                fontSize="sm"
                                color="gray.800"
                                mb="4px"
                              >
                                “
                                {reasonMessage ? (
                                  <FormattedMessage {...reasonMessage} />
                                ) : (
                                  item.reason
                                )}
                                ”
                              </Text>
                              <Text fontSize="sm" color="text">
                                <FormattedMessage
                                  {...commonMessages.selectionRateReportedTimes}
                                  values={{ count: item.count }}
                                />
                              </Text>
                            </Card>
                          );
                        })}
                      </Flex>
                    </Box>
                  )}

                  {/* Improve + goal */}
                  <Box pt={{ base: 5, md: 8 }}>
                    <ImproveAndGoal context="selection_rate" />
                  </Box>

                  {/* Academy link */}
                  <Link
                    as="a"
                    href={academyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Flex
                      cursor="pointer"
                      alignItems="center"
                      justifyContent="center"
                      mt={{ base: 5, md: 8 }}
                    >
                      <Icon
                        as={SolidInfoCircle}
                        marginRight="4px"
                        color="gray.500"
                      />
                      <Text
                        fontSize="md"
                        color="gray.500"
                        textDecoration="underline"
                        mb={0}
                      >
                        <FormattedMessage
                          {...messages.discover_more_selection}
                        />
                      </Text>
                    </Flex>
                  </Link>
                </Container>
              </WizardContent>
            </Wizard>
          </ThemeProvider>
        </CSSIsolation>
      </Other>
    </Match>
  );
};
